<template>
  <v-container v-if="!isLoading" fluid class="white px-0 elevation-4">
    <main-app-bar :title="title">
      <template v-slot:buttons>
        <v-btn class="mr-4" color="red" dark @click="deleteBtnHandler">Delete</v-btn>
        <v-btn class="mx-4" :to="{name: 'roles'}">Cancel</v-btn>
        <v-btn color="primary" @click="submitFormHandler">Save</v-btn>
      </template>
    </main-app-bar>
    <v-form ref="roleForm">
      <v-container class="px-5">
        <v-layout row px-4>
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>Title</p>
          </v-flex>
          <v-flex xs6>
            <v-text-field
              v-model="roleInfo.title"
              placeholder="Role Title"
              :rules="roleTitleRules"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <div class="font-weight-black mb-4">Account settings</div>
        <v-layout row px-4>
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>Role Level*</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">Defines if user works in Clinic, HQ or if user is an Admin. Admin level allows to grant Admin permissions: manage clinics, staff, task templates, roles. Technically there can be unlimited amount of Admins with full access.</p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6>
            <v-radio-group v-model="roleInfo.level" row>
              <v-radio
                v-for="roleGroup in roleGroups"
                :key="roleGroup.value"
                :label="roleGroup.title"
                :value="roleGroup.value"
              ></v-radio>
            </v-radio-group>
          </v-flex>
        </v-layout>
        <v-layout row px-4>
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>Sub-unit allowed</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">Employee of a role with this settings can be assigned to a specific Sub-unit as a default location (as opposed to Clinic). E.g. Receptionist probably doesn't need this setting.  A Nurse or a Dentist might require it as they work in Surgeries. When Sub-unit is set in User’s Account -  Employee will be put to that Sub-unit by default in the Rota.</p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6>
            <v-checkbox
              v-model="roleInfo.isSurgeryAllowed"
              :disabled="roleInfo.level === roles.groupAdmin"
            ></v-checkbox>
          </v-flex>
        </v-layout>
          <v-layout row px-4>
              <v-flex xs5 class="d-flex align-center pr-5">
                  <p>HR task assignment</p>
                  <v-tooltip top>
                      <template v-slot:activator="{ on }">
                          <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
                      </template>
                      <p class="tooltip-fixed">For this role HR tasks will be generated in HQ when "Create HR tasks" function is used.</p>
                  </v-tooltip>
              </v-flex>
              <v-flex xs6>
                  <v-checkbox mt-0
                              :disabled="roleInfo.level !== roles.hq"
                              v-model="roleInfo.hrTaskAssignment"
                  ></v-checkbox>
              </v-flex>
          </v-layout>
          <v-layout row px-4>
              <v-flex xs5 class="d-flex align-center pr-5">
                  <p>Hide tasks tab</p>
                  <v-tooltip top>
                      <template v-slot:activator="{ on }">
                          <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
                      </template>
                      <p class="tooltip-fixed">Users with this role will not see the tasks made for this role, if there are such.</p>
                  </v-tooltip>
              </v-flex>
              <v-flex xs6>
                  <v-checkbox mt-0
                              v-model="roleInfo.isHidden"
                  ></v-checkbox>
              </v-flex>
          </v-layout>
          <div class="font-weight-black mb-4">Supervised by</div>
        <v-layout row px-4 class="mb-4">
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>Tasks, SpotCheck</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">This setting will impact on which Roles can Spot Check tasks completed by the current Role. Overdue tasks assigned to the current role will appear for the Supervising role.
                E.g. for Nurse Role this field may have Practice Manager and Head Nurse selected. This means PM and NH will see Nurse’s tasks when they become overdue on a separate list. Also they will be able to Appraise the tasks completed by Nurse.  </p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6 class="d-flex align-center">
            <v-autocomplete
              multiple
              v-model="roleInfo.supervisors"
              :items="roleSupervisorList"
              item-text="title"
              item-value="id"
              :rules="supervisedRules"
              :search-input.sync="searchInputAutocomplete[searchInputConst.roles]"
              @change="clearSearchInput(searchInputConst.roles)"
              dense
              solo
              lighten-1
            ></v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout row px-4>
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>Leaves approval level 1</p>
          </v-flex>
          <v-flex xs6 class="d-flex align-center">
            <v-row class="align-center">
              <v-col>
                <v-autocomplete
                  multiple
                  v-model="leaveRequestFirstLevelSupervisors"
                  :items="roleSupervisorList"
                  :search-input.sync="searchInputAutocomplete[searchInputConst.firstLevelSearch]"
                  @change="clearSearchInput(searchInputConst.firstLevelSearch)"
                  item-text="title"
                  item-value="id"
                  dense
                  solo
                  lighten-1
                />
              </v-col>
              <v-col>
                <v-radio-group v-model="isGlobalFirstLevel" row dence hide-details class="my-0 py-0">
                  <v-radio
                    label="Local"
                    :value="false"
                  ></v-radio>
                  <v-radio
                    label="Global"
                    :value="true"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-flex>
        </v-layout>
        <v-layout row px-4 class="mb-4">
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>Leaves approval level 2*</p>
          </v-flex>
          <v-flex xs6 class="d-flex align-center">
            <v-row class="align-center">
              <v-col>
                <v-autocomplete
                  multiple
                  v-model="leaveRequestSecondLevelSupervisors"
                  :items="roleSupervisorList"
                  :rules="secondLevelRules"
                  :search-input.sync="searchInputAutocomplete[searchInputConst.secondLevelSearch]"
                  @change="clearSearchInput(searchInputConst.secondLevelSearch)"
                  item-text="title"
                  item-value="id"
                  dense
                  solo
                  lighten-1
                />
              </v-col>
              <v-col>
                <v-radio-group v-model="isGlobalSecondLevel" row dence hide-details class="my-0 py-0">
                  <v-radio
                    label="Local"
                    :value="false"
                  ></v-radio>
                  <v-radio
                    label="Global"
                    :value="true"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-flex>
        </v-layout>
        <div class="font-weight-black mb-4">Clinic Level Permissions</div>
        <v-layout row px-4 v-for="resp of currentClinicFunctions" :key="resp.id">
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>{{resp.title}}</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">{{resp.description}}</p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6>
            <v-checkbox mt-0
                        v-model="roleInfo.permissions"
                        :value="resp.id"
                        multiple
            ></v-checkbox>
          </v-flex>
        </v-layout>
        <div class="font-weight-black mb-4">Group Wide Permissions</div>
        <v-layout row px-4 v-for="resp of currentResponsibilities" :key="resp.id">
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>{{resp.title}}</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">{{resp.description}}</p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6>
            <v-checkbox mt-0
                        v-model="roleInfo.permissions"
                        :value="resp.id"
                        multiple
            ></v-checkbox>
          </v-flex>
        </v-layout>
        <div class="font-weight-black mb-4" v-if="currentCommonFunctions.length">Common Permissions</div>
        <v-layout row px-4 v-for="resp of currentCommonFunctions" :key="resp.id">
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>{{resp.title}}</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">{{resp.description}}</p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6>
            <v-checkbox mt-0
                        v-model="roleInfo.permissions"
                        :value="resp.id"
                        multiple
            ></v-checkbox>
          </v-flex>
        </v-layout>
        <div class="font-weight-black mb-4">Super Admin Permissions</div>
        <v-layout row px-4 v-for="resp of currentAdminFunctions" :key="resp.id">
          <v-flex xs5 class="d-flex align-center pr-5">
            <p>{{resp.title}}</p>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <p style="margin-left: 10px;"><v-icon color="grey" dark v-on="on">mdi-information</v-icon></p>
              </template>
              <p class="tooltip-fixed">{{resp.description}}</p>
            </v-tooltip>
          </v-flex>
          <v-flex xs6>
            <v-checkbox mt-0
                        v-model="roleInfo.permissions"
                        :value="resp.id"
                        :disabled="roleInfo.level !== roles.superAdmin"
                        multiple
            ></v-checkbox>
          </v-flex>
        </v-layout>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import validationRules from '@/common/validationRules'
  import MainAppBar from '@/components/MainAppBar'
  import enums from '@/common/enums'
  import selectInputAutocomplete from '../../constatns/selectInputAutocomplete'

  export default {
    data: () => ({
      roleInfo: {
        title: '',
        level: 'Clinic',
          hrTaskAssignment: false,
        // clinicRequired: false,
        isSurgeryAllowed: false,
        permissions: [],
        supervisors: [],
        isHidden: false
      },
      isLoading: true,
      title: '',
      roleTitleRules: validationRules.title,
      roleGroups: [{
        title: 'Clinic',
        value: enums.roles.clinic
      }, {
        title: 'HQ',
        value: enums.roles.hq
      }, {
        title: 'Group Admin',
        value: enums.roles.groupAdmin
      }, {
        title: 'Super Admin',
        value: enums.roles.superAdmin
      }],
      searchInputConst: selectInputAutocomplete,
      searchInputAutocomplete: {
        [selectInputAutocomplete.roles]: null,
        [selectInputAutocomplete.firstLevelSearch]: null,
        [selectInputAutocomplete.secondLevelSearch]: null
      },
      leaveRequestFirstLevelSupervisors: [],
      leaveRequestSecondLevelSupervisors: [],
      isGlobalFirstLevel: false,
      isGlobalSecondLevel: false,
      levelKeys: {
        FIRST: 'First',
        SECOND: 'Second'
      }
    }),
    computed: {
      ...mapGetters(['getRoleById', 'getResponsibilities', 'getAdminFunctions', 'getClinicFunctions', 'getCommonFunctions']),
      responsibilityList: function () {
        return this.getResponsibilities
      },
      roleSupervisorList: function () {
        return this.$store.getters.getRoles.filter(role => role.id !== this.roleId)
      },
      currentResponsibilities: function () {
        return this.responsibilityList.map(resp => {
          resp.isActive = this.roleInfo.permissions.includes(resp.id)
          return resp
        })
      },
      adminFunctionsList: function () {
        return this.getAdminFunctions
      },
      currentAdminFunctions: function () {
        return this.adminFunctionsList.map(resp => {
          resp.isActive = this.roleInfo.permissions.includes(resp.id)
          return resp
        })
      },
      clinicFunctionsList: function () {
        return this.getClinicFunctions
      },
      currentClinicFunctions: function () {
        return this.getClinicFunctions.map(resp => {
          resp.isActive = this.roleInfo.permissions.includes(resp.id)
          return resp
        })
      },
      commonFunctionsList: function () {
        return this.getCommonFunctions
      },
      currentCommonFunctions: function () {
        return this.getCommonFunctions.map(resp => {
          resp.isActive = this.roleInfo.permissions.includes(resp.id)
          return resp
        })
      },
      roles: function () {
        return enums.roles
      },
      supervisedRules () {
          const rules = []
          rules.push(val => {
              return (this.roleInfo.level === this.roles.groupAdmin && val.length > 0) ? 'Supervisors must be empty' : true
          })
          rules.push(val => {
              return (this.roleInfo.level === this.roles.superAdmin && val.length > 0) ? 'Supervisors must be empty' : true
          })
          return rules
      },
      secondLevelRules () {
        const rules = []
        rules.push(() => {
          return (this.leaveRequestSecondLevelSupervisors.length === 0) ? 'Required field' : true
        })
        return rules
      }
    },
    methods: {
      ...mapActions(['addRole', 'showAlert', 'fetchRoles', 'fetchPermissions', 'fetchRoleInfo', 'editRoleInfo', 'deleteRole']),
      setLeaveRequestSupervisors (data) {
        const firstLevel = data.filter(item => item.level === this.levelKeys.FIRST)
        if (firstLevel.length > 0) {
          firstLevel.forEach((item) => { this.leaveRequestFirstLevelSupervisors.push(item.id) })
          this.isGlobalFirstLevel = firstLevel[0].isGlobal
        }
        const secondLevel = data.filter(item => item.level === this.levelKeys.SECOND)
        if (secondLevel.length > 0) {
          secondLevel.forEach((item) => { this.leaveRequestSecondLevelSupervisors.push(item.id) })
          this.isGlobalSecondLevel = secondLevel[0].isGlobal
        }
      },
      getleaveRequestSupervisors () {
        const newLeaveRequestSupervisors = []
        if (this.leaveRequestFirstLevelSupervisors.length > 0) {
          this.leaveRequestFirstLevelSupervisors.forEach((item) => {
            newLeaveRequestSupervisors.push({
              id: item,
              isGlobal: this.isGlobalFirstLevel,
              level: this.levelKeys.FIRST
            })
          })
        }
        if (this.leaveRequestSecondLevelSupervisors.length > 0) {
          this.leaveRequestSecondLevelSupervisors.forEach((item) => {
            newLeaveRequestSupervisors.push({
              id: item,
              isGlobal: this.isGlobalSecondLevel,
              level: this.levelKeys.SECOND
            })
          })
        }
        return newLeaveRequestSupervisors
      },
      async submitFormHandler () {
        const id = this.$router.currentRoute.params.id
        if (this.$refs.roleForm.validate()) {
          if (this.roleInfo.isHidden) {
            const confirmModalOptions = {
              text: 'Hide tasks setting is enabled. Users with this role will not see the tasks made for this role, if there are such. Proceed saving?',
              agreeBtnText: 'Save',
              type: 'info'
            }
            if (id) {
              const confirm = await this.$root.$confirmDialog.open('', confirmModalOptions)
              if (!confirm) {
                return
              }
            }
          }
          this.roleInfo.leaveRequestSupervisors = this.getleaveRequestSupervisors()
          const formData = { ...this.roleInfo }
          // if (formData.level === this.roles.hq || formData.level === this.roles.groupAdmin) {
          //   formData.isSurgeryAllowed = false
          //   formData.dynamicDaylyRota = false
          // }
          try {
            if (id) {
              await this.editRoleInfo(formData)
            } else {
              await this.addRole(formData)
            }
          } catch (e) {
            console.warn(e)
          }
        } else {
          this.$store.dispatch('showAlert', {
            text: 'Please check the page for errors',
            type: 'error'
          })
        }
      },
      async deleteBtnHandler () {
        if (this.$router.currentRoute.params.id) {
          if (await this.$root.$confirmDialog.open(this.roleInfo.title, {
            text: 'Are you sure? All task templates for this role will be deactivated. All Pending tasks will be given Cancelled status. Employees with only this role will lose access to their clinics. Proceed?'
          })) {
            this.deleteRole(this.roleInfo.id)
          }
        }
      },
      clearSearchInput (typeSearch) {
        this.searchInputAutocomplete[typeSearch] = null
      }
    },
    async mounted () {
      const roleId = +this.$router.currentRoute.params.id
      if (roleId) {
        this.roleInfo = await this.fetchRoleInfo(roleId)
        this.setLeaveRequestSupervisors(this.roleInfo.leaveRequestSupervisors)
        Promise.all([this.fetchRoles(), this.fetchPermissions()]).then((val) => {
          // this.roleInfo = { ...this.roleInfo, ...this.fetchRoleInfo(roleId) }
          this.title = this.roleInfo.title
          this.isLoading = false
        }, (error) => {
          console.log(error)
          this.$store.dispatch('showAlert', {
            text: 'Server error',
            type: 'error'
          })
        })
      } else {
        this.title = 'Add role'
        await this.fetchRoles()
        await this.fetchPermissions()
        this.isLoading = false
      }
    },
    components: {
      MainAppBar
    }
  }

</script>
<style>
p.tooltip-fixed {
  max-width: 600px;
  width: 100%;
  margin-bottom: 0;
}
</style>
